import {AsyncPipe, NgClass, NgIf} from '@angular/common';
import {
  AfterViewInit,
  Component,
  inject,
  OnInit,
  signal,
  ViewChild,
  ViewContainerRef,
  ViewEncapsulation,
  WritableSignal
} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {MatDrawer, MatSidenavModule} from '@angular/material/sidenav';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {of, timer} from 'rxjs';
import {
  delayWhen,
  distinctUntilChanged,
  filter,
  map,
  pairwise,
  switchMap,
  tap
} from 'rxjs/operators';
import {
  IntercomService,
  PermissionsService,
  SessionService,
  TakeUntilComponentDestroy
} from '@q9elements/ui-kit/common';
import {PageLoaderComponent, SidebarModule} from '@q9elements/ui-kit/components';
import {
  NavigationService,
  PossibleNavigationsData
} from 'src/app/core/navigation/navigation.service';
import {AuthorisationService} from 'src/app/core/services/authorisation.service';
import {ClientEnvService} from 'src/app/core/services/client-env.service';
import {GenerationProgressService} from 'src/app/shared/service/generation-progress.service';
import {TrainingsService} from '../../core/services/trainings.service';
import {DiagramMetaStoreService} from '../../diagram-data/diagram-meta-store.service';
import {DiagramCanvasComponent} from '../../diagram-render/diagram-canvas/diagram-canvas.component';
import {GenerationProgressButtonComponent} from '../shared/generation-progress-button/generation-progress-button.component';
import {SidebarService} from '../sidebar/sidebar.service';
import {ToolbarStateService} from '../toolbar/toolbar-state.service';
import {DvGenerationStatusModule} from '../shared/dv-generation-status/dv-generation-status.module';
import {MainToolbarComponent} from '../toolbar/main-toolbar/main-toolbar.component';
import {BottomToolbarComponent} from '../toolbar/bottom-toolbar/bottom-toolbar.component';
import {LeftToolbarComponent} from '../toolbar/left-toolbar/left-toolbar.component';
import {SfaHeaderService} from '../../diagram-render/sfa-header/sfa-header.service';
import {SpinnerService} from '../../core/spinner/spinner.service';
import {DIAGRAM_VERSION_STATES_ENUM} from '../shared/models/diagram-version.constant';
import {MapRequestAccessService} from '../../core/map/map-request-access.service';
import {RecommendationsGenerationStatusComponent} from '../shared/recommendation-generation-status/recommendations-generation-status.component';
import {DiagramTypeConfigService} from '../../core/services/diagram-type-config.service';
import {ModeDataService} from '../../editor-state/mode-data.service';
import {DiagramUpdateService} from '../../diagram-data/diagram-update.service';

const FIVE_SECONDS = 1_000 * 5;
const delayForFiveSeconds = () => timer(FIVE_SECONDS);

@Component({
  selector: 'app-editor-interface',
  templateUrl: './editor-interface.component.html',
  styleUrls: ['./editor-interface.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [
    NgClass,
    MatSidenavModule,
    MatTooltipModule,
    DiagramCanvasComponent,
    NgIf,
    GenerationProgressButtonComponent,
    AsyncPipe,
    TranslateModule,
    DvGenerationStatusModule,
    MainToolbarComponent,
    BottomToolbarComponent,
    LeftToolbarComponent,
    SidebarModule,
    PageLoaderComponent,
    RecommendationsGenerationStatusComponent
  ]
})
export class EditorInterfaceComponent
  extends TakeUntilComponentDestroy
  implements OnInit, AfterViewInit
{
  @ViewChild('rightSidebar') rightSidebar!: MatDrawer;
  @ViewChild('lazy', {read: ViewContainerRef, static: true}) lazy: ViewContainerRef;

  public expanded$ = this.toolbarStateService.toolbarState$.pipe(map(state => state.expanded));
  public storyGenerationStatus$ = this.generationProgressService.storyGenerationStatus$.pipe(
    delayWhen(status => (status === null ? delayForFiveSeconds() : of(status)))
  );
  public isArchitecture = this.diagramTypeConfigSvc.isArchitecture;
  public recommendationsUPNGenerationProgress$ =
    this.generationProgressService.recommendationsUPNGenerationProgress$.pipe(
      distinctUntilChanged()
    );
  public recommendationsAgentInstructionsGenerationProgress$ =
    this.generationProgressService.recommendationsAgentInstructionsGenerationProgress$.pipe(
      distinctUntilChanged()
    );
  public recommendationsAgentFinderGenerationProgress$ =
    this.generationProgressService.recommendationsAgentFinderGenerationProgress$.pipe(
      distinctUntilChanged()
    );
  public sidebarSet = false;
  public dvGenerationInProgress$ = this.generationProgressService.dvGenerationInProgress$.pipe(
    distinctUntilChanged(),
    tap(inProgress => {
      if (inProgress !== undefined) {
        if (inProgress) {
          this.sidebarSvc.close('right');
        } else {
          this.sidebarSvc.toggle('right');
        }
      }
    })
  );

  protected readonly intercomService = inject(IntercomService);
  protected readonly sessionService = inject(SessionService);
  protected readonly permissionsService = inject(PermissionsService);
  protected readonly modeDataSvc = inject(ModeDataService);
  private readonly diagramUpdateSvc = inject(DiagramUpdateService);

  public isDraftState: WritableSignal<boolean> = signal(false);

  constructor(
    public generationProgressService: GenerationProgressService,
    public trainingsService: TrainingsService,
    public authorisationService: AuthorisationService,
    public sfaHeaderSvc: SfaHeaderService,
    public spinnerService: SpinnerService,
    private sidebarSvc: SidebarService,
    private toolbarStateService: ToolbarStateService,
    private diagramStoreService: DiagramMetaStoreService,
    private navSvc: NavigationService,
    private clientEnv: ClientEnvService,
    private diagramTypeConfigSvc: DiagramTypeConfigService,
    private mapRequestAccessService: MapRequestAccessService,
    private route: ActivatedRoute
  ) {
    super();
  }

  ngOnInit() {
    this.mapRequestAccessService.handlePresetUserInvite(this.route);
    this.navSvc.requestPossibleNavSubject
      .pipe(
        this.takeUntilComponentDestroy(),
        filter((data: PossibleNavigationsData) => Boolean(data.diagramId && data.state)),
        tap(({state}) => this.isDraftState.set(state === DIAGRAM_VERSION_STATES_ENUM.DRAFT)),
        switchMap(({diagramId, state}) =>
          this.diagramStoreService.preparePossibleNavigation(diagramId, state)
        )
      )
      .subscribe();

    this.modeDataSvc.currentMode$
      .pipe(this.takeUntilComponentDestroy())
      .subscribe(() => this.fireDiagramLoadedEvent());

    this.diagramUpdateSvc.readyForUpdate$
      .pipe(
        this.takeUntilComponentDestroy(),
        pairwise(),
        filter(([prev, curr]) => !prev && curr)
      )
      .subscribe(() => {
        if (this.modeDataSvc.isEditMode()) {
          this.fireDraftChangedEvent();
        }
      });
  }

  private fireDiagramLoadedEvent(): void {
    const event = this.modeDataSvc.isEditMode()
      ? this.intercomService.EVENTS_BY_AREA.DIAGRAMS.DIAGRAM_EDITED
      : this.intercomService.EVENTS_BY_AREA.DIAGRAMS.DIAGRAM_VIEWED;

    this.intercomService.fireEvent('ACTION', event, {
      space_id: this.sessionService.teamId(),
      actor_user_id: this.sessionService.userId(),
      account_id: this.permissionsService.team()?.domain,
      event_date: new Date().toISOString(),
      product_area: this.intercomService.PRODUCT_AREAS.DIAGRAMS
    });
  }

  private fireDraftChangedEvent(): void {
    this.intercomService.fireEvent(
      'ACTION',
      this.intercomService.EVENTS_BY_AREA.DIAGRAMS.DIAGRAM_DRAFT_CHANGED,
      {
        space_id: this.sessionService.teamId(),
        actor_user_id: this.sessionService.userId(),
        account_id: this.permissionsService.team()?.domain,
        event_date: new Date().toISOString(),
        product_area: this.intercomService.PRODUCT_AREAS.DIAGRAMS
      }
    );
  }

  ngAfterViewInit() {
    timer(0).subscribe(() => this.sidebarSvc.setup(null, this.rightSidebar));
    this.clientEnv.sidebarModuleSubject$
      .pipe(
        this.takeUntilComponentDestroy(),
        filter(state => state && !this.sidebarSet),
        tap(() => (this.sidebarSet = true))
      )
      .subscribe(() => this.lazyLoadModule());
  }

  toggleRight() {
    this.sidebarSvc.toggle('right');
  }

  private async lazyLoadModule(): Promise<void> {
    const {RightSidebarComponent} = await import(
      'src/app/editor-interface/sidebar/right-sidebar/right-sidebar.component'
    );

    this.lazy.remove();
    this.lazy.createComponent(RightSidebarComponent);
  }
}
